import './CareerForm.css';
import React, {useLayoutEffect, useRef, useState} from 'react';
import {
    CAREER_ADMIN_MESSAGE_SUBJECT,
    CAREER_ADMIN_TEMPLATE,
    CAREER_MESSAGE_FROM,
    CAREER_USER_MESSAGE_SUBJECT,
    CAREER_USER_TEMPLATE
} from "../../../constants/forms/career-form/CareerFormConstants";
import {
    createFormData,
    getAdminEmail,
    printConsoleLog,
    redirectPage,
    scrollFormsViewToTop,
    submitFormDataAsObject,
    validateFileSize,
    validateTextarea
} from "../../../utils/Utils";
import CircularPageLoader from '../../common/CircularPageLoader';
import {useNavigate} from 'react-router-dom';
import {parseFormSubmitNotifyPayload} from '../../../utils/formAdminNotify';

const CareerForm = () => {
    const navigate = useNavigate();

    const [file, setFile] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [countryCode, setCountryCode] = useState('90'); // Set initial state to Türkiye's code
    const [phone, setPhone] = useState('');
    // const [city, setCity] = useState('');
    const [state, setState] = useState('');
    // const [country, setCountry] = useState('');
    const [message, setMessage] = useState('');
    const [submitInProgress, setSubmitInProgress] = useState(false);
    const isSubmittingRef = useRef(false);
    const submitButtonRef = useRef(null);

    useLayoutEffect(() => {
        if (!submitInProgress) {
            return;
        }
        scrollFormsViewToTop();
        const raf = requestAnimationFrame(() => {
            scrollFormsViewToTop();
        });
        return () => cancelAnimationFrame(raf);
    }, [submitInProgress]);

    const clearForm = () => {
        setFile('');
        setFirstName("");
        setLastName("");
        setEmail("");
        setCountryCode("");
        setPhone("");
        // setCity("");
        setState("");
        // setCountry("");
        setMessage("");
    };

    const handleFileChange = (event) => {
        if (validateFileSize(event.target.files[0].size, 'formFile', 'errorValidateFileSize')) {
            setFile(event.target.files[0]);
        }
    };

    const handleFirstNameChange = (event) => {
        setFirstName(event.target.value);
    };

    const handleLastNameChange = (event) => {
        setLastName(event.target.value);
    };

    const handleEmailChange = (event) => {
        setEmail(event.target.value);
    };

    const handleCountryCodeChange = (event) => {
        setCountryCode(event.target.value);
    };

    const handlePhoneChange = (event) => {
        const input = event.target.value;

        // Remove non-numeric characters
        const cleaned = input.replace(/\D/g, '');

        // Limit the input to 10 digits
        const slicedInput = cleaned.slice(0, 10);

        // Format the phone number dynamically
        let formattedPhone = '';

        for (let i = 0; i < slicedInput.length; i++) {
            if (i === 0) {
                formattedPhone += `(${slicedInput[i]}`;
            } else if (i === 2) {
                formattedPhone += `${slicedInput[i]}) `;
            } else if (i === 5) {
                formattedPhone += `${slicedInput[i]}-`;
            } else {
                formattedPhone += slicedInput[i];
            }
        }

        // Update the state with the dynamically formatted phone number
        setPhone(formattedPhone);
    };

    const handleKeyDown = (event) => {
        // Allow backspace (keyCode 8) to remove characters
        if (event.keyCode === 8) {
            setPhone("");
        }
    };

    const handleStateChange = (event) => {
        setState(event.target.value);
    };

    const handleMessageChange = (event) => {
        setMessage(event.target.value);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isSubmittingRef.current) {
            return;
        }

        // Validation to prevent user enter "script, iframe" tags
        if (!(validateTextarea("formMessage", "errorValidateTextarea"))) {
            return;
        }

        isSubmittingRef.current = true;
        const submitBtn = submitButtonRef.current;
        if (submitBtn) {
            submitBtn.disabled = true;
        }
        setSubmitInProgress(true);
        scrollFormsViewToTop();
        requestAnimationFrame(() => {
            scrollFormsViewToTop();
        });

        try {
            const phoneFinal = '+' + countryCode + ' ' + phone;
            const fullName = firstName + " " + lastName;

            const adminTemplate = CAREER_ADMIN_TEMPLATE(fullName, email, phone, "--", state, "Türkiye", message);
            const userTemplate = CAREER_USER_TEMPLATE(firstName);

            const careerData = createFormData(
                file,
                CAREER_MESSAGE_FROM,
                CAREER_MESSAGE_FROM,
                getAdminEmail(),
                email,
                CAREER_ADMIN_MESSAGE_SUBJECT(fullName),
                CAREER_USER_MESSAGE_SUBJECT,
                adminTemplate,
                userTemplate,
                firstName,
                lastName,
                email,
                phoneFinal,
                '--',
                state,
                "Türkiye",
                message
            );

            // send career emails
            const emailResponse =
                await submitFormDataAsObject(careerData, '/email-sender/career-email');

            let innerJson = null;
            try {
                innerJson = emailResponse.text ? JSON.parse(emailResponse.text) : null;
            } catch (_) {
                /* ignore */
            }

            printConsoleLog(innerJson?.status ?? emailResponse.status, innerJson?.message);

            const submitSucceeded =
                emailResponse.status === 200 || Number(innerJson?.status) === 200;
            const adminNotify = parseFormSubmitNotifyPayload(emailResponse, innerJson);

            clearForm();
            if (submitSucceeded) {
                navigate('/success', {state: {adminNotify}});
            } else {
                redirectPage('/failed');
            }
        } catch (err) {
            console.error('Career form submit failed', err);
            redirectPage('/failed');
        } finally {
            setSubmitInProgress(false);
            isSubmittingRef.current = false;
            if (submitBtn) {
                submitBtn.disabled = false;
            }
        }
    };

    return (
        <>
            <div
                className="mt-3"
                style={{
                    maxWidth: '900px',
                    margin: '0 auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingRight: '10px',
                    paddingLeft: '10px',
                }}
            >
                {submitInProgress ? (
                    <CircularPageLoader
                        title="Gönderiliyor…"
                        hint="Başvurunuz ve özgeçmiş dosyanız iletiliyor. Lütfen sayfayı kapatmayınız."
                        ariaLabel="Kariyer formu gönderiliyor"
                    />
                ) : (
                <form onSubmit={handleSubmit} encType="multipart/form-data">

                    <div className="row">
                        <div className="col-sm">
                            <div className="mb-3">
                                <label htmlFor="formFirstName"
                                       className="form-label camp-burgundy required">
                                    Adınız
                                </label>
                                <input
                                    className="form-control"
                                    type="text"
                                    pattern="^(?!.*(<script|<iframe)\b)[\s\S]*$"
                                    id="formFirstName"
                                    name="firstName"
                                    value={firstName}
                                    onChange={handleFirstNameChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="col-sm">
                            <div className="mb-3">
                                <label htmlFor="formLastName"
                                       className="form-label camp-burgundy required">
                                    Soyadınız
                                </label>
                                <input
                                    className="form-control"
                                    type="text"
                                    pattern="^(?!.*(<script|<iframe)\b)[\s\S]*$"
                                    id="formLastName"
                                    name="lastName"
                                    value={lastName}
                                    onChange={handleLastNameChange}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="formEmail"
                               className="form-label camp-burgundy required">
                            E-posta adresiniz
                        </label>
                        <input
                            className="form-control"
                            type="email"
                            pattern="^(?!.*(<script|<iframe)\b)[\s\S]*$"
                            id="formEmail"
                            name="email"
                            value={email}
                            onChange={handleEmailChange}
                            required
                        />
                    </div>


                    <div className="row">
                        <div className="col-sm-4 mb-3">
                            <label htmlFor="formCountryCode" className="form-label camp-burgundy required">
                                Ülke Kodu
                            </label>
                            <select
                                className="form-select"
                                id="formCountryCode"
                                name="countryCode"
                                onChange={handleCountryCodeChange}
                                required
                            >
                                <option value="" hidden>...</option>
                                <option value="90" selected>Türkiye (+90)</option>
                                <option value="213">Algeria (+213)</option>
                                <option value="376">Andorra (+376)</option>
                                <option value="244">Angola (+244)</option>
                                <option value="1264">Anguilla (+1264)</option>
                                <option value="1268">Antigua &amp; Barbuda (+1268)</option>
                                <option value="54">Argentina (+54)</option>
                                <option value="374">Armenia (+374)</option>
                                <option value="297">Aruba (+297)</option>
                                <option value="61">Australia (+61)</option>
                                <option value="43">Austria (+43)</option>
                                <option value="994">Azerbaijan (+994)</option>
                                <option value="1242">Bahamas (+1242)</option>
                                <option value="973">Bahrain (+973)</option>
                                <option value="880">Bangladesh (+880)</option>
                                <option value="1246">Barbados (+1246)</option>
                                <option value="375">Belarus (+375)</option>
                                <option value="32">Belgium (+32)</option>
                                <option value="501">Belize (+501)</option>
                                <option value="229">Benin (+229)</option>
                                <option value="1441">Bermuda (+1441)</option>
                                <option value="975">Bhutan (+975)</option>
                                <option value="591">Bolivia (+591)</option>
                                <option value="387">Bosnia Herzegovina (+387)</option>
                                <option value="267">Botswana (+267)</option>
                                <option value="55">Brazil (+55)</option>
                                <option value="673">Brunei (+673)</option>
                                <option value="359">Bulgaria (+359)</option>
                                <option value="226">Burkina Faso (+226)</option>
                                <option value="257">Burundi (+257)</option>
                                <option value="855">Cambodia (+855)</option>
                                <option value="237">Cameroon (+237)</option>
                                <option value="1">Canada (+1)</option>
                                <option value="238">Cape Verde Islands (+238)</option>
                                <option value="1345">Cayman Islands (+1345)</option>
                                <option value="236">Central African Republic (+236)</option>
                                <option value="56">Chile (+56)</option>
                                <option value="86">China (+86)</option>
                                <option value="57">Colombia (+57)</option>
                                <option value="269">Comoros (+269)</option>
                                <option value="242">Congo (+242)</option>
                                <option value="682">Cook Islands (+682)</option>
                                <option value="506">Costa Rica (+506)</option>
                                <option value="385">Croatia (+385)</option>
                                <option value="53">Cuba (+53)</option>
                                <option value="90392">Cyprus North (+90392)</option>
                                <option value="357">Cyprus South (+357)</option>
                                <option value="42">Czech Republic (+42)</option>
                                <option value="45">Denmark (+45)</option>
                                <option value="253">Djibouti (+253)</option>
                                <option value="1809">Dominica (+1809)</option>
                                <option value="1809">Dominican Republic (+1809)</option>
                                <option value="593">Ecuador (+593)</option>
                                <option value="20">Egypt (+20)</option>
                                <option value="503">El Salvador (+503)</option>
                                <option value="240">Equatorial Guinea (+240)</option>
                                <option value="291">Eritrea (+291)</option>
                                <option value="372">Estonia (+372)</option>
                                <option value="251">Ethiopia (+251)</option>
                                <option value="500">Falkland Islands (+500)</option>
                                <option value="298">Faroe Islands (+298)</option>
                                <option value="679">Fiji (+679)</option>
                                <option value="358">Finland (+358)</option>
                                <option value="33">France (+33)</option>
                                <option value="594">French Guiana (+594)</option>
                                <option value="689">French Polynesia (+689)</option>
                                <option value="241">Gabon (+241)</option>
                                <option value="220">Gambia (+220)</option>
                                <option value="7880">Georgia (+7880)</option>
                                <option value="49">Germany (+49)</option>
                                <option value="233">Ghana (+233)</option>
                                <option value="350">Gibraltar (+350)</option>
                                <option value="30">Greece (+30)</option>
                                <option value="299">Greenland (+299)</option>
                                <option value="1473">Grenada (+1473)</option>
                                <option value="590">Guadeloupe (+590)</option>
                                <option value="671">Guam (+671)</option>
                                <option value="502">Guatemala (+502)</option>
                                <option value="224">Guinea (+224)</option>
                                <option value="245">Guinea - Bissau (+245)</option>
                                <option value="592">Guyana (+592)</option>
                                <option value="509">Haiti (+509)</option>
                                <option value="504">Honduras (+504)</option>
                                <option value="852">Hong Kong (+852)</option>
                                <option value="36">Hungary (+36)</option>
                                <option value="354">Iceland (+354)</option>
                                <option value="91">India (+91)</option>
                                <option value="62">Indonesia (+62)</option>
                                <option value="98">Iran (+98)</option>
                                <option value="964">Iraq (+964)</option>
                                <option value="353">Ireland (+353)</option>
                                <option value="972">Israel (+972)</option>
                                <option value="39">Italy (+39)</option>
                                <option value="1876">Jamaica (+1876)</option>
                                <option value="81">Japan (+81)</option>
                                <option value="962">Jordan (+962)</option>
                                <option value="7">Kazakhstan (+7)</option>
                                <option value="254">Kenya (+254)</option>
                                <option value="686">Kiribati (+686)</option>
                                <option value="850">Korea North (+850)</option>
                                <option value="82">Korea South (+82)</option>
                                <option value="965">Kuwait (+965)</option>
                                <option value="996">Kyrgyzstan (+996)</option>
                                <option value="856">Laos (+856)</option>
                                <option value="371">Latvia (+371)</option>
                                <option value="961">Lebanon (+961)</option>
                                <option value="266">Lesotho (+266)</option>
                                <option value="231">Liberia (+231)</option>
                                <option value="218">Libya (+218)</option>
                                <option value="417">Liechtenstein (+417)</option>
                                <option value="370">Lithuania (+370)</option>
                                <option value="352">Luxembourg (+352)</option>
                                <option value="853">Macao (+853)</option>
                                <option value="389">Macedonia (+389)</option>
                                <option value="261">Madagascar (+261)</option>
                                <option value="265">Malawi (+265)</option>
                                <option value="60">Malaysia (+60)</option>
                                <option value="960">Maldives (+960)</option>
                                <option value="223">Mali (+223)</option>
                                <option value="356">Malta (+356)</option>
                                <option value="692">Marshall Islands (+692)</option>
                                <option value="596">Martinique (+596)</option>
                                <option value="222">Mauritania (+222)</option>
                                <option value="269">Mayotte (+269)</option>
                                <option value="52">Mexico (+52)</option>
                                <option value="691">Micronesia (+691)</option>
                                <option value="373">Moldova (+373)</option>
                                <option value="377">Monaco (+377)</option>
                                <option value="976">Mongolia (+976)</option>
                                <option value="1664">Montserrat (+1664)</option>
                                <option value="212">Morocco (+212)</option>
                                <option value="258">Mozambique (+258)</option>
                                <option value="95">Myanmar (+95)</option>
                                <option value="264">Namibia (+264)</option>
                                <option value="674">Nauru (+674)</option>
                                <option value="977">Nepal (+977)</option>
                                <option value="31">Netherlands (+31)</option>
                                <option value="687">New Caledonia (+687)</option>
                                <option value="64">New Zealand (+64)</option>
                                <option value="505">Nicaragua (+505)</option>
                                <option value="227">Niger (+227)</option>
                                <option value="234">Nigeria (+234)</option>
                                <option value="683">Niue (+683)</option>
                                <option value="672">Norfolk Islands (+672)</option>
                                <option value="670">Northern Marianas (+670)</option>
                                <option value="47">Norway (+47)</option>
                                <option value="968">Oman (+968)</option>
                                <option value="680">Palau (+680)</option>
                                <option value="507">Panama (+507)</option>
                                <option value="675">Papua New Guinea (+675)</option>
                                <option value="595">Paraguay (+595)</option>
                                <option value="51">Peru (+51)</option>
                                <option value="63">Philippines (+63)</option>
                                <option value="48">Poland (+48)</option>
                                <option value="351">Portugal (+351)</option>
                                <option value="1787">Puerto Rico (+1787)</option>
                                <option value="974">Qatar (+974)</option>
                                <option value="262">Reunion (+262)</option>
                                <option value="40">Romania (+40)</option>
                                <option value="7">Russia (+7)</option>
                                <option value="250">Rwanda (+250)</option>
                                <option value="378">San Marino (+378)</option>
                                <option value="239">Sao Tome &amp; Principe (+239)</option>
                                <option value="966">Saudi Arabia (+966)</option>
                                <option value="221">Senegal (+221)</option>
                                <option value="381">Serbia (+381)</option>
                                <option value="248">Seychelles (+248)</option>
                                <option value="232">Sierra Leone (+232)</option>
                                <option value="65">Singapore (+65)</option>
                                <option value="421">Slovak Republic (+421)</option>
                                <option value="386">Slovenia (+386)</option>
                                <option value="677">Solomon Islands (+677)</option>
                                <option value="252">Somalia (+252)</option>
                                <option value="27">South Africa (+27)</option>
                                <option value="34">Spain (+34)</option>
                                <option value="94">Sri Lanka (+94)</option>
                                <option value="290">St. Helena (+290)</option>
                                <option value="1869">St. Kitts (+1869)</option>
                                <option value="1758">St. Lucia (+1758)</option>
                                <option value="249">Sudan (+249)</option>
                                <option value="597">Suriname (+597)</option>
                                <option value="268">Swaziland (+268)</option>
                                <option value="46">Sweden (+46)</option>
                                <option value="41">Switzerland (+41)</option>
                                <option value="963">Syria (+963)</option>
                                <option value="886">Taiwan (+886)</option>
                                <option value="7">Tajikstan (+7)</option>
                                <option value="66">Thailand (+66)</option>
                                <option value="228">Togo (+228)</option>
                                <option value="676">Tonga (+676)</option>
                                <option value="1868">Trinidad &amp; Tobago (+1868)</option>
                                <option value="216">Tunisia (+216)</option>
                                <option value="7">Turkmenistan (+7)</option>
                                <option value="993">Turkmenistan (+993)</option>
                                <option value="1649">Turks &amp; Caicos Islands (+1649)</option>
                                <option value="688">Tuvalu (+688)</option>
                                <option value="256">Uganda (+256)</option>
                                <option value="44">UK (+44)</option>
                                <option value="380">Ukraine (+380)</option>
                                <option value="971">United Arab Emirates (+971)</option>
                                <option value="598">Uruguay (+598)</option>
                                <option value="1">USA (+1)</option>
                                <option value="7">Uzbekistan (+7)</option>
                                <option value="678">Vanuatu (+678)</option>
                                <option value="379">Vatican City (+379)</option>
                                <option value="58">Venezuela (+58)</option>
                                <option value="84">Vietnam (+84)</option>
                                <option value="84">Virgin Islands - British (+1284)</option>
                                <option value="84">Virgin Islands - US (+1340)</option>
                                <option value="681">Wallis &amp; Futuna (+681)</option>
                                <option value="969">Yemen (North)(+969)</option>
                                <option value="967">Yemen (South)(+967)</option>
                                <option value="260">Zambia (+260)</option>
                                <option value="263">Zimbabwe (+263)</option>
                            </select>
                        </div>

                        <div className="col-sm-8 mb-3">
                            <label htmlFor="formPhone" className="form-label camp-burgundy required">
                                İletişim Numarası
                            </label>
                            <input
                                className="form-control"
                                type="text"
                                placeholder="(555) 555-5555"
                                value={phone}
                                onChange={handlePhoneChange}
                                onKeyDown={handleKeyDown}
                                required
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="formCity"
                               className="form-label camp-burgundy required">
                            İkamet ettiğiniz şehir
                        </label>
                        <select
                            className="form-select"
                            id="formCity"
                            name="city"
                            value={state}
                            onChange={handleStateChange}
                            required
                        >
                            <option value="">Lütfen bir şehir seçiniz...</option>
                            <option value="Adana">Adana</option>
                            <option value="Adıyaman">Adıyaman</option>
                            <option value="Afyonkarahisar">Afyonkarahisar</option>
                            <option value="Ağrı">Ağrı</option>
                            <option value="Amasya">Amasya</option>
                            <option value="Ankara">Ankara</option>
                            <option value="Antalya">Antalya</option>
                            <option value="Artvin">Artvin</option>
                            <option value="Aydın">Aydın</option>
                            <option value="Balıkesir">Balıkesir</option>
                            <option value="Bilecik">Bilecik</option>
                            <option value="Bingöl">Bingöl</option>
                            <option value="Bitlis">Bitlis</option>
                            <option value="Bolu">Bolu</option>
                            <option value="Burdur">Burdur</option>
                            <option value="Bursa">Bursa</option>
                            <option value="Çanakkale">Çanakkale</option>
                            <option value="Çankırı">Çankırı</option>
                            <option value="Çorum">Çorum</option>
                            <option value="Denizli">Denizli</option>
                            <option value="Diyarbakır">Diyarbakır</option>
                            <option value="Edirne">Edirne</option>
                            <option value="Elazığ">Elazığ</option>
                            <option value="Erzincan">Erzincan</option>
                            <option value="Erzurum">Erzurum</option>
                            <option value="Eskişehir">Eskişehir</option>
                            <option value="Gaziantep">Gaziantep</option>
                            <option value="Giresun">Giresun</option>
                            <option value="Gümüşhane">Gümüşhane</option>
                            <option value="Hakkâri">Hakkâri</option>
                            <option value="Hatay">Hatay</option>
                            <option value="Isparta">Isparta</option>
                            <option value="Mersin">Mersin</option>
                            <option value="İstanbul">İstanbul</option>
                            <option value="İzmir">İzmir</option>
                            <option value="Kars">Kars</option>
                            <option value="Kastamonu">Kastamonu</option>
                            <option value="Kayseri">Kayseri</option>
                            <option value="Kırklareli">Kırklareli</option>
                            <option value="Kırşehir">Kırşehir</option>
                            <option value="Kocaeli">Kocaeli</option>
                            <option value="Konya">Konya</option>
                            <option value="Kütahya">Kütahya</option>
                            <option value="Malatya">Malatya</option>
                            <option value="Manisa">Manisa</option>
                            <option value="Kahramanmaraş">Kahramanmaraş</option>
                            <option value="Mardin">Mardin</option>
                            <option value="Muğla">Muğla</option>
                            <option value="Muş">Muş</option>
                            <option value="Nevşehir">Nevşehir</option>
                            <option value="Niğde">Niğde</option>
                            <option value="Ordu">Ordu</option>
                            <option value="Rize">Rize</option>
                            <option value="Sakarya">Sakarya</option>
                            <option value="Samsun">Samsun</option>
                            <option value="Siirt">Siirt</option>
                            <option value="Sinop">Sinop</option>
                            <option value="Sivas">Sivas</option>
                            <option value="Tekirdağ">Tekirdağ</option>
                            <option value="Tokat">Tokat</option>
                            <option value="Trabzon">Trabzon</option>
                            <option value="Tunceli">Tunceli</option>
                            <option value="Şanlıurfa">Şanlıurfa</option>
                            <option value="Uşak">Uşak</option>
                            <option value="Van">Van</option>
                            <option value="Yozgat">Yozgat</option>
                            <option value="Zonguldak">Zonguldak</option>
                            <option value="Aksaray">Aksaray</option>
                            <option value="Bayburt">Bayburt</option>
                            <option value="Karaman">Karaman</option>
                            <option value="Kırıkkale">Kırıkkale</option>
                            <option value="Batman">Batman</option>
                            <option value="Şırnak">Şırnak</option>
                            <option value="Bartın">Bartın</option>
                            <option value="Ardahan">Ardahan</option>
                            <option value="Iğdır">Iğdır</option>
                            <option value="Yalova">Yalova</option>
                            <option value="Karabük">Karabük</option>
                            <option value="Kilis">Kilis</option>
                            <option value="Osmaniye">Osmaniye</option>
                            <option value="Düzce">Düzce</option>
                        </select>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="fullNameInput"
                               className="form-label camp-burgundy required">
                            Özgeçmişinizi (CV / Resume) yükleyiniz
                        </label>
                        <p>PDF formatında ve en fazla 10MB boyutunda olmalıdır.</p>
                        <input
                            className="form-control"
                            type="file"
                            accept="application/pdf"
                            max=""
                            pattern="^(?!.*(<script|<iframe)\b)[\s\S]*$"
                            id="formFile"
                            name="file"
                            onChange={handleFileChange}
                            required
                        />
                        <p id="errorValidateFileSize" className="error-text mt-3">
                            Hata: Dosya en fazla 10MB boyutunda olmalıdır.
                        </p>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="formMessage" className="form-label camp-burgundy">
                            Bize iletmek istediğiniz bir mesajınız var mı? (isteğe bağlı)
                        </label>
                        <p>Bu mesajda çalışmak istediğiniz alan hakkında talepte bulunabilirsiniz.</p>
                        <p>Faaliyetlerimizde hangi konu(lar)da faydalı olabileceğinizi düşünüyorsunuz?
                            Yeni fikirlere açık olduğumuzu belirtmek isteriz.
                            İlginiz için şimdiden teşekkürler...
                        </p>
                        <textarea
                            className="form-control"
                            id="formMessage"
                            name="message"
                            onChange={handleMessageChange}
                            rows="4"
                        />
                    </div>

                    <div className="form-group form-check mb-3 mt-5">
                        <input type="checkbox" className="form-check-input" id="exampleCheck1" required/>
                        <label className="form-check-label" htmlFor="exampleCheck1">Kişisel verilerin işlenmesine
                            ilişkin <a
                                target="_blank"
                                rel="noreferrer"
                                href="https://www.campegocamp.com/kvkk-gizlilik-politikam%C4%B1z"
                                className="text-decoration-none" style={{color: '#A60000'}}>Gizlilik Politikası</a>nı okudum,
                            anladım ve onaylıyorum.</label>
                    </div>

                    {/* Validation Error Message */}
                    <p id="errorValidateTextarea" className="error-text">
                        Mesaj alanında geçersiz karakter(ler) tespit edildi!
                        Lütfen geçerli karakter(ler) giriniz.
                    </p>

                    {/* Submit Button */}
                    <button ref={submitButtonRef} type="submit" className="btn camp-btn-primary">Gönder</button>

                </form>
                )}
            </div>
        </>
    );
};

export default CareerForm;
